import { Typography } from 'app/components/ui';
import Pagination from 'app/components/Pagination';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { useMemo, useState } from 'react';
import { i18n } from 'app/utils/i18n';
import { usePagination } from 'app/utils/hooks/usePagination';
import { OverviewTable } from './OverviewTable/OverviewTable';
import { OverviewHeader } from './OverviewHeader/OverviewHeader';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { useTagsByDocument, useDocumentTaxonomyConfig } from 'app/api/tag-queries';
import { FilterGroup, useFilteredTags } from 'app/components/editor/sidebar/tags/TagsFilter';
import { formatTaxonomyName } from 'app/utils/tag-utils';

import './Overview.scss';

export const Overview = () => {
  const [show, setShow] = useState(false);
  const { pDocument } = useEditorSelector((editor: any) => editor);

  const { data: { tags, taxonomy } = { tags: [], taxonomy: {} } } = useTagsByDocument(show ? pDocument?.superId : undefined, {
    include: ['metadata', 'concept', 'taxonomy'],
  });
  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(show ? pDocument?.superId : undefined);
  const totalTags = tags.length;

  const tagsWithTaxonomy = useMemo(() => tags.map((tag) => ({ ...tag, taxonomy: taxonomy[tag.taxonomyId] })), [tags, taxonomy]);

  const totalMandatoryTags = useMemo(() => Object.values(taxonomy).reduce((sum, t) => sum + (t?.totalMandatoryTags ?? 0), 0), [taxonomy]);

  const stats = useMemo(() => {
    const counts = { approved: 0, mandatory: 0, draft: 0, needsAttention: 0, invalid: 0 };
    for (const tag of tags) {
      if (tag.status === 'approved') counts.approved++;
      else if (tag.status === 'draft') counts.draft++;
      else if (tag.status === 'need_attention') counts.needsAttention++;
      if (tag.validationStatus === 'invalid') counts.invalid++;
      if (tag.concept?.isMandatory) counts.mandatory++;
    }
    return [
      { label: i18n('tag.total.tags'), value: totalTags, type: 'total' },
      { label: i18n('tag.mandatory.label'), value: counts.mandatory, type: 'mandatory' },
      { label: i18n('tag.status.approved'), value: counts.approved, type: 'approved' },
      { label: i18n('tag.status.draft'), value: counts.draft, type: 'draft' },
      { label: i18n('tag.status.need_attention'), value: counts.needsAttention, type: 'need_attention' },
      { label: i18n('tag.status.invalid'), value: counts.invalid, type: 'invalid' },
    ];
  }, [tags, totalTags]);

  const taxonomyFilterOptions = useMemo(() => {
    if (!taxonomyConfig?.taxonomySelections?.length) return [];
    return taxonomyConfig.taxonomySelections.map((ts) => ({
      label: ts.taxonomy?.name ? formatTaxonomyName(ts.taxonomy.name) : ts.taxonomyId,
      value: ts.taxonomy?.name || ts.taxonomyId,
    }));
  }, [taxonomyConfig?.taxonomySelections]);

  const filterGroups: FilterGroup[] = [
    {
      label: i18n('tag.taxonomy.plural'),
      value: 'taxonomy',
      icon: <i className="fa-solid fa-layer-group" />,
      options: taxonomyFilterOptions,
    },
    {
      label: i18n('tag.highlighted.option.status'),
      value: 'status',
      icon: <i className="fa-solid fa-spinner" />,
      options: [
        { label: i18n('tag.status.approved'), value: 'approved' },
        { label: i18n('tag.status.draft'), value: 'draft' },
        { label: i18n('tag.status.need_attention'), value: 'need_attention' },
        { label: i18n('tag.status.verified'), value: 'verified' },
      ],
    },
    {
      label: i18n('tag.validation.label'),
      value: 'validated',
      icon: <i className="fa-solid fa-flag" />,
      options: [
        { label: i18n('tag.status.invalid'), value: 'invalid' },
        { label: i18n('tag.status.unvalidated'), value: 'unvalidated' },
        { label: i18n('tag.status.valid'), value: 'valid' },
      ],
    },
    {
      label: i18n('tag.type'),
      value: 'type',
      icon: <i className="fa-solid fa-tags" />,
      options: [
        { label: i18n('tag.type.numeric'), value: 'nonFraction' },
        { label: i18n('tag.type.textBlock'), value: 'nonNumeric' },
      ],
    },
  ];

  const { filters, filteredTags, onAddFilter, onRemoveFilter, searchInput, setSearchInput } = useFilteredTags(tagsWithTaxonomy, filterGroups);

  const { paginatedItems, page, setCurrentPage, pageSize } = usePagination(filteredTags, { pageSize: 5 });

  const tagCategories = [
    i18n('tag.name'),
    i18n('tag.type'),
    i18n('taxonomy.title'),
    i18n('tag.highlighted.option.status'),
    i18n('tag.validation.label'),
    i18n('tag.mandatory.label'),
    i18n('all.tags.page'),
  ];

  return (
    <>
      <button className="tag-toolbar__button" onClick={() => setShow(true)}>
        <i className="fa-regular fa-lg fa-eye" />
        <Typography variant="body-5" fontWeight="bold">
          {i18n('tag.overview.label')}
        </Typography>
      </button>

      <BootstrapModal show={show} onHide={() => setShow(false)} size="xl" contentClassName="overview-modal">
        <BootstrapModal.Header className="overview-modal__header">
          <OverviewHeader
            stats={stats}
            totalMandatoryTags={totalMandatoryTags}
            totalTags={totalTags}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onClose={() => setShow(false)}
            filters={filters}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            filterGroups={filterGroups}
          />
        </BootstrapModal.Header>

        <BootstrapModal.Body className="overview-modal__body">
          <div className="overview-modal__table-wrapper">
            <OverviewTable categories={tagCategories} tableData={paginatedItems} onNavigate={() => setShow(false)} />
          </div>
        </BootstrapModal.Body>

        <div className="overview-modal__footer">
          <Pagination onPaginate={(p: number) => setCurrentPage(p)} page={page} pageSize={pageSize} />
        </div>
      </BootstrapModal>
    </>
  );
};
